<!--
  成就配置面板。

  把原来硬编码在 AchievementCatalog 里的 56 条成就搬到了后台，支持
  新增 / 编辑 / 删除 / 上下架 / 解锁试算。

  「条件可视化配置」的实现方式：管理员只挑一个**指标** + 填一个**阈值**，
  条件类型（累计值 / 单日峰值 / 连续区间 / 时段命中…）、统计时间窗口、进度单位
  全部由指标带出并只读展示。这样既做到了可视化，又不可能配出
  「对累计时长取连续天数」这类无意义组合 —— 指标背后的采集逻辑只能靠改代码扩展。

  条件模式分两种：
    · SINGLE 单条件 —— 一个指标 + 一个阈值，落 metric_key / target_value / params；
    · RULE   组合条件 —— 多个指标用「且 / 或」组合，落 rule_json。
  两条路径在服务端共用同一个评估器（叶子就是单条件），所以阈值含义不会漂移。
  可视化编辑器只表达「单层 AND / OR」；遇到多层嵌套会退化为 JSON 直接编辑，
  而不是硬拆成平铺行 —— 硬拆会静默丢掉结构，保存一次就把规则改坏了。

  ⚠️ 成就编码创建后不可修改：user_achievements 按 code 关联，
     改码等于把历史解锁记录变成无主数据。
  ⚠️ 已有人解锁过的成就不能删除，只能下架。
-->
<template>
  <div>
    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />成就列表
          <span class="adm-card-title-extra">
            已上架 {{ stats.enabled }} · 已下架 {{ stats.disabled }}
          </span>
        </div>
        <div class="adm-head-actions">
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="load">
            刷新
          </el-button>
          <el-button size="small" type="primary" icon="el-icon-plus" @click="openCreate">
            新增成就
          </el-button>
        </div>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="query.keyword"
          class="adm-w200"
          placeholder="编码 / 名称"
          clearable
          name="achKeyword"
          autocomplete="off"
          @keyup.enter.native="onSearch"
        />
        <el-select v-model="query.status" class="adm-w130" placeholder="全部状态" clearable>
          <el-option label="已上架" value="ENABLED" />
          <el-option label="已下架" value="DISABLED" />
        </el-select>
        <el-select v-model="query.categoryCode" class="adm-w140" placeholder="全部分类" clearable>
          <el-option
            v-for="item in meta.categories"
            :key="item.code"
            :label="item.label"
            :value="item.code"
          />
        </el-select>
        <el-button type="primary" icon="el-icon-search" @click="onSearch">查询</el-button>
      </div>

      <el-table v-loading="loading" :data="rows" border>
        <el-table-column label="成就" min-width="200">
          <template slot-scope="scope">
            <div class="ach-head">
              <FaIcon :icon="iconOf(scope.row.icon)" :size="14" class="ach-icon" />
              <span class="ach-name">{{ scope.row.name }}</span>
              <el-tag v-if="scope.row.hidden" size="mini" type="info">隐藏</el-tag>
            </div>
            <div class="adm-code">{{ scope.row.code }}</div>
          </template>
        </el-table-column>

        <el-table-column label="分类" width="92">
          <template slot-scope="scope">{{ scope.row.categoryLabel }}</template>
        </el-table-column>

        <el-table-column label="等级" width="88">
          <template slot-scope="scope">
            <span class="level-dot" :style="{ background: scope.row.levelColor }" />
            {{ scope.row.levelLabel }}
          </template>
        </el-table-column>

        <el-table-column label="解锁条件" min-width="250">
          <template slot-scope="scope">
            <template v-if="scope.row.conditionMode === 'RULE'">
              <div class="cond-main">
                组合条件
                <span class="cond-tag">{{ scope.row.ruleLeafCount }} 个判定条件</span>
              </div>
              <div class="cond-sub">
                主指标 {{ scope.row.metricLabel || '（指标已失效）' }}
              </div>
            </template>
            <template v-else>
              <div class="cond-main">
                {{ scope.row.metricLabel || '（指标已失效）' }}
                <span v-if="scope.row.conditionTypeLabel" class="cond-tag">
                  {{ scope.row.conditionTypeLabel }}
                </span>
              </div>
              <div class="cond-sub">
                阈值 {{ targetText(scope.row) }}
                <template v-if="scope.row.windowLabel">· 窗口 {{ scope.row.windowLabel }}</template>
              </div>
            </template>
          </template>
        </el-table-column>

        <el-table-column label="解锁数" width="88" align="right">
          <template slot-scope="scope">
            <span class="adm-num">{{ scope.row.unlockedCount }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="86">
          <template slot-scope="scope">
            <el-tag size="mini" :type="scope.row.status === 'ENABLED' ? 'success' : 'info'">
              {{ scope.row.statusLabel }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <div class="adm-op-actions">
              <el-link
                type="primary"
                :underline="false"
                @click="openEdit(scope.row)"
              >编辑</el-link>
              <el-link
                type="primary"
                :underline="false"
                @click="onToggleStatus(scope.row)"
              >{{ scope.row.status === 'ENABLED' ? '下架' : '上架' }}</el-link>
              <el-link
                type="primary"
                :underline="false"
                @click="openPreview(scope.row)"
              >试算</el-link>
              <el-tooltip
                :disabled="scope.row.deletable"
                content="已有人解锁，只能下架"
                placement="top"
              >
                <span class="adm-op-item">
                  <el-link
                    type="danger"
                    :underline="false"
                    :disabled="!scope.row.deletable"
                    @click="onDelete(scope.row)"
                  >删除</el-link>
                </span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="!loading && !rows.length" class="adm-empty">
        <i class="el-icon-medal" />没有匹配的成就
      </div>

      <AdmPager
        :total="total"
        :page="query.page"
        :size="query.size"
        @page-change="onPageChange"
        @size-change="onSizeChange"
      />
    </div>

    <!-- ---------------- 新增 / 编辑 ---------------- -->
    <el-dialog
      :title="form.id ? '编辑成就' : '新增成就'"
      :visible.sync="formVisible"
      width="720px"
      append-to-body
    >
      <el-form label-width="112px">
        <el-form-item label="成就编码">
          <el-input
            v-model="form.code"
            :disabled="!!form.id"
            placeholder="大写字母 / 数字 / 下划线，如 STUDY_2000H"
            name="achCode"
            autocomplete="off"
          />
          <div class="adm-inline-hint">
            {{ form.id
              ? '编码创建后不可修改：历史解锁记录按编码关联。'
              : '编码是成就的永久身份，发布后不可修改、也不可复用。' }}
          </div>
        </el-form-item>

        <el-form-item label="名称">
          <el-input v-model="form.name" maxlength="50" show-word-limit name="achName" autocomplete="off" />
        </el-form-item>

        <el-form-item label="描述">
          <el-input
            v-model="form.description"
            maxlength="200"
            show-word-limit
            name="achDesc"
            autocomplete="off"
          />
        </el-form-item>

        <el-form-item label="分类 / 等级">
          <div class="inline-row">
            <el-select v-model="form.categoryCode" class="adm-w180" placeholder="选择分类">
              <el-option
                v-for="item in meta.categories"
                :key="item.code"
                :label="item.label"
                :value="item.code"
              />
            </el-select>
            <el-select v-model="form.levelCode" class="adm-w160">
              <el-option
                v-for="item in meta.levels"
                :key="item.code"
                :label="item.label"
                :value="item.code"
              />
            </el-select>
          </div>
        </el-form-item>

        <el-form-item label="条件模式">
          <el-radio-group v-model="form.conditionMode" @change="onModeChange">
            <el-radio-button
              v-for="item in meta.conditionModes"
              :key="item.code"
              :label="item.code"
            >{{ item.label }}</el-radio-button>
          </el-radio-group>
          <div class="adm-inline-hint">{{ conditionModeHint }}</div>
        </el-form-item>

        <el-form-item v-if="isSingleMode" label="判定指标">
          <el-select
            v-model="form.metricKey"
            class="adm-w-full"
            filterable
            placeholder="选择指标（决定条件怎么算）"
            @change="onMetricChange"
          >
            <el-option
              v-for="item in meta.metrics"
              :key="item.key"
              :label="item.label"
              :value="item.key"
            >
              <span>{{ item.label }}</span>
              <span class="option-dim">{{ item.conditionTypeLabel }}</span>
            </el-option>
          </el-select>
          <div v-if="selectedMetric" class="metric-info">
            <div class="metric-line">
              条件类型：<b>{{ selectedMetric.conditionTypeLabel }}</b>
              <span class="metric-dim">{{ selectedMetric.conditionTypeDescription }}</span>
            </div>
            <div class="metric-line">
              统计窗口：<b>{{ selectedMetric.windowLabel }}</b>
              <span class="metric-dim">{{ selectedMetric.description }}</span>
            </div>
          </div>
        </el-form-item>

        <el-form-item v-if="isSingleMode && selectedMetric && selectedMetric.categoryConfigurable" label="统计类别">
          <el-select v-model="form.blockCategory" class="adm-w180" placeholder="选择类别">
            <el-option
              v-for="item in meta.blockCategories"
              :key="item.code"
              :label="item.label"
              :value="item.code"
            />
          </el-select>
          <span class="adm-inline-hint is-inline">按标签关键词识别学习 / 睡眠 / 其他</span>
        </el-form-item>

        <el-form-item v-if="isSingleMode && selectedMetric && selectedMetric.windowConfigurable" label="统计时段">
          <div class="inline-row">
            <el-time-select
              v-model="form.windowStart"
              class="adm-w130"
              :picker-options="{ start: '00:00', step: '00:15', end: '23:45' }"
              placeholder="开始"
            />
            <span class="adm-inline-hint is-inline">至</span>
            <el-time-select
              v-model="form.windowEnd"
              class="adm-w130"
              :picker-options="{ start: '00:00', step: '00:15', end: '23:45' }"
              placeholder="结束"
            />
          </div>
          <div class="adm-inline-hint">
            按时间块的「开始时间」是否落在该时段内筛选（含首尾）。
            <template v-if="selectedMetric && selectedMetric.categoryConfigurable">
              与上面的「统计类别」同时生效，再按阈值累计。
            </template>
          </div>
        </el-form-item>

        <el-form-item v-if="isSingleMode" label="解锁阈值">
          <el-input-number v-model="form.targetValue" :min="1" :step="1" :precision="0" />
          <span v-if="selectedMetric" class="adm-inline-hint is-inline">{{ selectedMetric.targetHint }}</span>
        </el-form-item>

        <!-- ---------------- 组合条件（AND / OR 条件树） ---------------- -->
        <template v-if="!isSingleMode">
          <el-form-item label="组合方式">
            <el-radio-group v-model="form.ruleOperator">
              <el-radio-button
                v-for="item in meta.ruleOperators"
                :key="item.code"
                :label="item.code"
              >{{ item.label }}</el-radio-button>
            </el-radio-group>
            <div class="adm-inline-hint">{{ ruleOperatorHint }}</div>
          </el-form-item>

          <el-form-item v-if="form.ruleAdvanced" label="条件结构（高级）">
            <el-input
              v-model="form.ruleJsonText"
              type="textarea"
              :rows="9"
              placeholder='{"op":"AND","children":[{"metric":"STUDY_TOTAL_MINUTES","target":3000}]}'
            />
            <div class="adm-inline-hint">
              该成就的条件结构较复杂，已切换为高级编辑方式，请按上方示例的格式填写。
              叶子数上限 {{ maxRuleLeaves }} 个、嵌套上限 {{ meta.maxRuleDepth || 3 }} 层。
            </div>
          </el-form-item>

          <el-form-item v-else label="判定条件">
            <div class="rule-rows">
              <div v-for="(leaf, index) in form.ruleLeaves" :key="index" class="rule-row">
                <div class="rule-row-head">
                  <span class="rule-row-index">{{ index + 1 }}</span>
                  <span v-if="index > 0" class="rule-row-op">{{ ruleOperatorLabel }}</span>
                  <el-link
                    type="danger"
                    :underline="false"
                    class="rule-row-remove"
                    @click="removeLeaf(index)"
                  >移除</el-link>
                </div>
                <div class="rule-row-body">
                  <el-select
                    v-model="leaf.metricKey"
                    class="rule-metric"
                    filterable
                    placeholder="选择指标"
                    @change="onLeafMetricChange(leaf)"
                  >
                    <el-option
                      v-for="item in meta.metrics"
                      :key="item.key"
                      :label="item.label"
                      :value="item.key"
                    >
                      <span>{{ item.label }}</span>
                      <span class="option-dim">{{ item.conditionTypeLabel }}</span>
                    </el-option>
                  </el-select>

                  <el-select
                    v-if="leafMetric(leaf) && leafMetric(leaf).categoryConfigurable"
                    v-model="leaf.blockCategory"
                    class="adm-w130"
                    placeholder="类别"
                  >
                    <el-option
                      v-for="item in meta.blockCategories"
                      :key="item.code"
                      :label="item.label"
                      :value="item.code"
                    />
                  </el-select>

                  <template v-if="leafMetric(leaf) && leafMetric(leaf).windowConfigurable">
                    <el-time-select
                      v-model="leaf.windowStart"
                      class="adm-w120"
                      :picker-options="timePickerOptions"
                      placeholder="开始"
                    />
                    <span class="adm-inline-hint is-inline">至</span>
                    <el-time-select
                      v-model="leaf.windowEnd"
                      class="adm-w120"
                      :picker-options="timePickerOptions"
                      placeholder="结束"
                    />
                  </template>

                  <el-input-number
                    v-model="leaf.targetValue"
                    class="rule-target"
                    :min="1"
                    :step="1"
                    :precision="0"
                  />
                  <span class="adm-inline-hint is-inline">{{ leafTargetHint(leaf) }}</span>
                </div>
              </div>
            </div>

            <div class="rule-actions">
              <el-button
                size="mini"
                icon="el-icon-plus"
                :disabled="form.ruleLeaves.length >= maxRuleLeaves"
                @click="addLeaf"
              >添加条件</el-button>
              <span class="adm-inline-hint is-inline">
                最多 {{ maxRuleLeaves }} 个条件，每个条件都是一条独立的指标判定
              </span>
            </div>
          </el-form-item>
        </template>

        <el-form-item label="图标">
          <el-select v-model="form.icon" class="adm-w-full" filterable>
            <el-option
              v-for="item in meta.icons"
              :key="item.key"
              :label="item.label + '（' + item.key + '）'"
              :value="item.key"
            />
          </el-select>
          <div class="icon-preview">
            <FaIcon :icon="iconOf(form.icon)" :size="20" />
          </div>
        </el-form-item>

        <el-form-item label="解锁条件文案">
          <el-input
            v-model="form.conditionText"
            maxlength="200"
            show-word-limit
            placeholder="例如：累计学习 2000 小时即可解锁"
            name="achCondition"
            autocomplete="off"
          />
        </el-form-item>

        <el-form-item label="展示顺序">
          <el-input-number v-model="form.sortNo" :min="0" :step="1" :precision="0" />
          <span class="adm-inline-hint is-inline">越小越靠前</span>
        </el-form-item>

        <el-form-item label="隐藏成就">
          <el-switch v-model="form.hidden" />
          <span class="adm-inline-hint is-inline">开启后解锁前只显示「???」</span>
        </el-form-item>

        <el-form-item label="备注">
          <el-input
            v-model="form.remark"
            maxlength="500"
            show-word-limit
            placeholder="仅供后台查看"
            name="achRemark"
            autocomplete="off"
          />
        </el-form-item>

        <el-form-item v-if="!form.id" label="立即上架">
          <el-switch v-model="form.enabled" />
          <span class="adm-inline-hint is-inline">默认保存为下架态草稿，确认无误后再上架</span>
        </el-form-item>
      </el-form>

      <div slot="footer">
        <el-button size="small" @click="formVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="saving" @click="onSubmit">保存</el-button>
      </div>
    </el-dialog>

    <!-- ---------------- 解锁试算 ---------------- -->
    <el-dialog title="解锁试算" :visible.sync="previewVisible" width="560px" append-to-body>
      <div class="adm-alert">
        <i class="el-icon-info" />
        <span>只读试算：按当前配置计算该用户此刻的指标值，<b>不会写入解锁记录</b>。</span>
      </div>

      <el-form label-width="92px" class="adm-preview-form">
        <el-form-item label="用户 ID">
          <el-input
            v-model="previewUserId"
            placeholder="填入要试算的用户 ID"
            name="achPreviewUser"
            autocomplete="off"
            @keyup.enter.native="onPreview"
          />
        </el-form-item>
      </el-form>

      <div v-if="preview" class="adm-desc adm-preview-result">
        <div class="adm-desc-item">
          <span class="adm-desc-label">成就</span>
          <span class="adm-desc-value">{{ preview.code }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">用户</span>
          <span class="adm-desc-value">
            {{ preview.userNickname || '（无昵称）' }} · {{ preview.userId }}
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">指标</span>
          <span class="adm-desc-value">{{ preview.metricLabel }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">当前值</span>
          <span class="adm-desc-value">
            <b class="adm-preview-strong">{{ preview.progressValue }}</b>
            <span class="adm-sub">/ {{ preview.targetValue }}</span>
          </span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">结论</span>
          <span class="adm-desc-value">
            <span v-if="preview.alreadyUnlocked">该用户已解锁过这条成就</span>
            <span v-else-if="!preview.enabled">成就当前已下架，不会真的解锁</span>
            <span v-else-if="preview.reached" class="adm-preview-ok">
              已达到阈值，访问成就页时会解锁
            </span>
            <span v-else class="adm-sub">尚未达到阈值</span>
          </span>
        </div>
      </div>

      <div slot="footer">
        <el-button size="small" @click="previewVisible = false">关闭</el-button>
        <el-button size="small" type="primary" :loading="previewing" @click="onPreview">
          试算
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FaIcon from '@/components/common/FaIcon.vue'
import AdmPager from '@/components/admin/AdmPager.vue'
import { resolveAchievementIcon } from '@/components/achievement/achievementIcons.js'
import {
  fetchAchievements,
  fetchAchievementMeta,
  createAchievement,
  updateAchievement,
  changeAchievementStatus,
  deleteAchievement,
  previewAchievement,
  isOk
} from '@/api/admin'

/** 空表单。⚠️ Vue 2 只有初始化时就存在的字段才是响应式的，字段必须全部列出。 */
function emptyForm() {
  return {
    id: null,
    code: '',
    name: '',
    description: '',
    categoryCode: '',
    levelCode: 1,
    metricKey: '',
    targetValue: 1,
    icon: 'star',
    hidden: false,
    sortNo: 0,
    conditionText: '',
    remark: '',
    enabled: false,
    // 参数化指标（时段 / 类别）用，HH:mm 字符串 + 类别码
    windowStart: '00:00',
    windowEnd: '05:00',
    blockCategory: 'ANY',
    // 条件模式：SINGLE 单条件 / RULE 组合条件
    conditionMode: 'SINGLE',
    ruleOperator: 'AND',
    // 组合条件的每一行（可视化编辑，单层 AND / OR）
    ruleLeaves: [],
    // 多层嵌套时退化为直接编辑 JSON，避免可视化编辑器把结构改坏
    ruleAdvanced: false,
    ruleJsonText: ''
  }
}

/** 新建一条空的组合条件行。 */
function emptyLeaf() {
  return {
    metricKey: '',
    targetValue: 1,
    windowStart: '00:00',
    windowEnd: '05:00',
    blockCategory: 'ANY'
  }
}

export default {
  name: 'AchievementsPanel',
  components: { FaIcon, AdmPager },
  data() {
    return {
      loading: false,
      saving: false,
      rows: [],
      total: 0,
      stats: { enabled: 0, disabled: 0 },
      query: { keyword: '', status: '', categoryCode: '', page: 1, size: 20 },
      meta: {
        categories: [],
        levels: [],
        metrics: [],
        icons: [],
        statuses: [],
        blockCategories: [],
        conditionModes: [],
        ruleOperators: [],
        maxRuleLeaves: 8,
        maxRuleDepth: 3
      },
      timePickerOptions: { start: '00:00', step: '00:15', end: '23:45' },
      // 与后端 AchievementRuleJson.MAX_LENGTH / rule_json 列宽保持一致
      maxRuleJsonLength: 4000,
      formVisible: false,
      form: emptyForm(),
      previewVisible: false,
      previewing: false,
      previewUserId: '',
      currentPreviewId: null,
      preview: null
    }
  },
  computed: {
    selectedMetric() {
      return this.meta.metrics.find(item => item.key === this.form.metricKey) || null
    },
    isSingleMode() {
      return this.form.conditionMode !== 'RULE'
    },
    maxRuleLeaves() {
      return Number(this.meta.maxRuleLeaves || 8)
    },
    ruleOperatorLabel() {
      const item = this.meta.ruleOperators.find(op => op.code === this.form.ruleOperator)
      return item ? item.label : ''
    },
    ruleOperatorHint() {
      const item = this.meta.ruleOperators.find(op => op.code === this.form.ruleOperator)
      return item ? item.description : ''
    },
    conditionModeHint() {
      const item = this.meta.conditionModes.find(mode => mode.code === this.form.conditionMode)
      return item ? item.description : ''
    }
  },
  created() {
    this.loadMeta()
    this.load()
  },
  methods: {
    loadMeta() {
      fetchAchievementMeta()
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '元数据加载失败')
          }
          const data = res.data || {}
          // ⚠️ 后端把 Long 序列化成字符串（JacksonConfig 全局行为），
          //    levelCode 必须转回数字，否则 el-select 的选项值 "1" 与
          //    表单里的数字 1 不相等，等级下拉会显示为空。
          this.meta = {
            categories: data.categories || [],
            levels: (data.levels || []).map(item => ({ ...item, code: Number(item.code) })),
            metrics: data.metrics || [],
            icons: data.icons || [],
            statuses: data.statuses || [],
            blockCategories: data.blockCategories || [],
            conditionModes: data.conditionModes || [],
            ruleOperators: data.ruleOperators || [],
            maxRuleLeaves: Number(data.maxRuleLeaves || 8),
            maxRuleDepth: Number(data.maxRuleDepth || 3)
          }
        })
        .catch(err => {
          this.$message.error(err.message || '元数据加载失败')
        })
    },
    load() {
      this.loading = true
      fetchAchievements(this.query)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '加载失败')
          }
          const data = res.data || {}
          this.rows = data.list || []
          // total 是 Long，后端序列化成字符串，统一转成数字
          this.total = Number(data.total || 0)
          this.stats = {
            enabled: Number(data.enabledCount || 0),
            disabled: Number(data.disabledCount || 0)
          }
        })
        .catch(err => {
          this.$message.error(err.message || '加载失败')
        })
        .finally(() => {
          this.loading = false
        })
    },
    onSearch() {
      this.query.page = 1
      this.load()
    },
    onPageChange(page) {
      this.query.page = page
      this.load()
    },
    onSizeChange(size) {
      this.query.size = size
      this.query.page = 1
      this.load()
    },
    iconOf(key) {
      return resolveAchievementIcon(key)
    },
    /** 阈值展示：HOUR 类指标的阈值以分钟存储，这里换算回小时。 */
    targetText(row) {
      const value = Number(row.targetValue || 0)
      switch (row.unit) {
        case 'HOUR':
          return `${value % 60 === 0 ? value / 60 : (value / 60).toFixed(1)} 小时`
        case 'DAY':
          return `${value} 天`
        case 'COUNT':
          return `${value} 次`
        case 'FRIEND':
          return `${value} 位好友`
        case 'WEEK':
          return `${value} 周`
        case 'PERCENT':
          return `${value}%`
        default:
          return '无（布尔型）'
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.formVisible = true
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        code: row.code,
        name: row.name,
        description: row.description || '',
        categoryCode: row.categoryCode,
        levelCode: Number(row.levelCode || 1),
        metricKey: row.metricKey,
        targetValue: Number(row.targetValue || 1),
        icon: row.icon,
        hidden: !!row.hidden,
        sortNo: Number(row.sortNo || 0),
        conditionText: row.conditionText || '',
        remark: row.remark || '',
        enabled: row.status === 'ENABLED',
        ...this.parseParams(row.params),
        ...this.parseRuleForForm(row.ruleJson)
      }
      this.formVisible = true
    },
    /**
     * 指标变化时把阈值 / 类别拉回合法值。
     *
     * 布尔型指标（单位 NONE）的取值只有 0 / 1，阈值必须恰好是 1；百分比型上限 100 ——
     * 后端也会拦（9516），这里先一步纠正，省掉一次无谓的报错。
     */
    onMetricChange() {
      const metric = this.selectedMetric
      if (!metric) return
      if (metric.unit === 'NONE') {
        this.form.targetValue = 1
      } else if (metric.unit === 'PERCENT' && Number(this.form.targetValue) > 100) {
        this.form.targetValue = 100
      }
      if (!metric.categoryConfigurable) {
        this.form.blockCategory = 'ANY'
      }
    },

    // ---------------- 组合条件 ----------------

    /** 切换到组合条件时，把当前单条件带成第一行，省掉一次重复选择。 */
    onModeChange(mode) {
      if (mode === 'RULE' && !this.form.ruleLeaves.length) {
        this.form.ruleLeaves = [{
          metricKey: this.form.metricKey,
          targetValue: Number(this.form.targetValue || 1),
          windowStart: this.form.windowStart,
          windowEnd: this.form.windowEnd,
          blockCategory: this.form.blockCategory
        }]
      }
    },
    addLeaf() {
      if (this.form.ruleLeaves.length >= this.maxRuleLeaves) return
      this.form.ruleLeaves.push(emptyLeaf())
    },
    removeLeaf(index) {
      this.form.ruleLeaves.splice(index, 1)
    },
    leafMetric(leaf) {
      return this.meta.metrics.find(item => item.key === leaf.metricKey) || null
    },
    leafTargetHint(leaf) {
      const metric = this.leafMetric(leaf)
      return metric ? metric.targetHint : ''
    },
    /** 与 onMetricChange 同一套纠正规则，只是作用在某一行上。 */
    onLeafMetricChange(leaf) {
      const metric = this.leafMetric(leaf)
      if (!metric) return
      if (metric.unit === 'NONE') {
        leaf.targetValue = 1
      } else if (metric.unit === 'PERCENT' && Number(leaf.targetValue) > 100) {
        leaf.targetValue = 100
      }
      if (!metric.categoryConfigurable) {
        leaf.blockCategory = 'ANY'
      }
    },
    /**
     * 把后端返回的 rule_json 解析回表单。
     *
     * <p>只展开「单层 AND / OR」——也就是可视化编辑器能表达的形状。
     * 遇到多层嵌套（或压根解析不了的脏 JSON）时退化为 JSON 直接编辑，
     * 而不是硬拆成一行行 —— 硬拆会静默丢掉嵌套结构，保存一次就把规则改坏了。</p>
     */
    parseRuleForForm(ruleJson) {
      const single = {
        conditionMode: 'SINGLE',
        ruleOperator: 'AND',
        ruleLeaves: [],
        ruleAdvanced: false,
        ruleJsonText: ''
      }
      if (!ruleJson) return single
      let tree = null
      try {
        tree = JSON.parse(ruleJson)
      } catch (err) {
        tree = null
      }
      const advanced = { ...single, conditionMode: 'RULE', ruleAdvanced: true, ruleJsonText: ruleJson }
      if (!tree || typeof tree !== 'object' || !Array.isArray(tree.children)) {
        return advanced
      }
      // 每个子节点都没有 op 才算「单层」
      const flat = !tree.op || tree.children.every(item => item && !item.op)
      if (!flat) {
        return advanced
      }
      return {
        conditionMode: 'RULE',
        ruleOperator: tree.op || 'AND',
        ruleLeaves: tree.children.map(item => ({
          metricKey: item.metric || '',
          targetValue: Number(item.target || 1),
          ...this.parseParams(this.leafParamsText(item.params))
        })),
        ruleAdvanced: false,
        ruleJsonText: ''
      }
    },
    /** 叶子上的 params 既可能是嵌套对象，也可能是 JSON 字符串，统一成字符串。 */
    leafParamsText(params) {
      if (!params) return ''
      return typeof params === 'string' ? params : JSON.stringify(params)
    },
    /** 取条件树的第一个叶子（含多层嵌套），用于推导列表页展示用的主指标。 */
    firstLeafOf(node, depth) {
      // depth 护栏：ruleJsonText 是手输的，超深嵌套会让递归爆栈把页面卡死
      const level = depth || 0
      if (!node || typeof node !== 'object' || level > 32) return null
      if (!node.op) return node
      if (!Array.isArray(node.children) || !node.children.length) return null
      return this.firstLeafOf(node.children[0], level + 1)
    },
    /** 当前表单对应的「主条件」。 */
    primaryLeaf() {
      if (this.form.ruleAdvanced) {
        const text = this.form.ruleJsonText.trim()
        // 与后端 AchievementRuleJson.MAX_LENGTH 对齐，超长的连解析都不必尝试
        if (text.length > this.maxRuleJsonLength) return null
        let tree = null
        try {
          tree = JSON.parse(text)
        } catch (err) {
          tree = null
        }
        const first = this.firstLeafOf(tree, 0)
        if (!first || !first.metric) return null
        return { metricKey: first.metric, targetValue: Number(first.target || 1) }
      }
      return this.form.ruleLeaves.length ? this.form.ruleLeaves[0] : null
    },
    /** 生成 rule_json；单条件模式返回空串（后端据此走老路径）。 */
    buildRuleJson() {
      if (this.form.conditionMode !== 'RULE') return ''
      if (this.form.ruleAdvanced) return this.form.ruleJsonText.trim()
      const children = this.form.ruleLeaves.map(leaf => {
        const item = { metric: leaf.metricKey, target: Number(leaf.targetValue) }
        const params = this.buildParamsFrom(this.leafMetric(leaf), leaf)
        if (params) item.params = params
        return item
      })
      return JSON.stringify({ op: this.form.ruleOperator, children })
    },
    /** 组合条件的逐行校验，与单条件模式同一套规则。 */
    validateRule() {
      if (this.form.ruleAdvanced) {
        const text = this.form.ruleJsonText.trim()
        if (!text) return '请填写条件结构'
        if (text.length > this.maxRuleJsonLength) {
          return `条件结构最长 ${this.maxRuleJsonLength} 个字符`
        }
        try {
          JSON.parse(text)
        } catch (err) {
          return '条件结构格式有误，请检查后重试'
        }
        if (!this.primaryLeaf()) return '条件结构里至少要有一个完整条件'
        return ''
      }
      const leaves = this.form.ruleLeaves
      if (leaves.length < 2) {
        return '组合条件至少需要 2 个判定条件；只有一个条件请改用单条件模式'
      }
      for (let i = 0; i < leaves.length; i++) {
        const leaf = leaves[i]
        const label = `第 ${i + 1} 个条件`
        if (!leaf.metricKey) return `${label}：请选择判定指标`
        const metric = this.leafMetric(leaf)
        if (!leaf.targetValue || Number(leaf.targetValue) < 1) return `${label}：阈值至少为 1`
        if (metric && metric.unit === 'NONE' && Number(leaf.targetValue) !== 1) {
          return `${label}：布尔型指标的阈值只能填 1`
        }
        if (metric && metric.unit === 'PERCENT' && Number(leaf.targetValue) > 100) {
          return `${label}：百分比型指标的阈值不能超过 100`
        }
        if (metric && metric.categoryConfigurable && !leaf.blockCategory) {
          return `${label}：请选择统计类别`
        }
        if (metric && metric.windowConfigurable) {
          const start = this.toMinutes(leaf.windowStart)
          const end = this.toMinutes(leaf.windowEnd)
          if (start === null || end === null) return `${label}：请选择统计时段`
          if (start >= end) return `${label}：时段的开始时间必须早于结束时间`
        }
      }
      return ''
    },
    buildPayload() {
      const ruleJson = this.buildRuleJson()
      // 组合条件时「主指标 / 阈值」取第一个条件：后端也会用第一个条件覆盖这两个字段，
      // 这里先对齐，只是为了让 @NotBlank / @NotNull 这类必填校验一次通过。
      const primary = ruleJson ? this.primaryLeaf() : null
      const payload = {
        code: this.form.code.trim(),
        name: this.form.name.trim(),
        description: this.form.description.trim(),
        categoryCode: this.form.categoryCode,
        levelCode: this.form.levelCode,
        metricKey: primary ? primary.metricKey : this.form.metricKey,
        targetValue: primary ? Number(primary.targetValue) : this.form.targetValue,
        icon: this.form.icon,
        hidden: this.form.hidden,
        sortNo: this.form.sortNo,
        conditionText: this.form.conditionText.trim(),
        remark: this.form.remark.trim(),
        enabled: this.form.enabled,
        // 空串是「未配置」的等价语义：MP 默认 updateStrategy=NOT_NULL，
        // 传 null 不会写库，会把上一次的组合条件留在行里。
        ruleJson
      }
      // 只有参数化指标才携带 params；其余指标不传，后端会置空。
      // 组合条件模式下 params 由后端从第一个条件带出，不必重复传。
      const params = ruleJson ? '' : this.buildParams()
      if (params) {
        payload.params = params
      }
      return payload
    },
    /** HH:mm → 分钟 of day，非法返回 null。 */
    toMinutes(text) {
      const parts = String(text || '').split(':')
      if (parts.length !== 2) return null
      const h = Number(parts[0])
      const m = Number(parts[1])
      if (Number.isNaN(h) || Number.isNaN(m)) return null
      return h * 60 + m
    },
    /** 分钟 of day → HH:mm。 */
    minutesToText(minutes) {
      const value = Number(minutes)
      if (Number.isNaN(value)) return '00:00'
      const h = Math.floor(value / 60)
      const m = value % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    /**
     * 把表单里的时段 / 类别序列化成后端约定的 params JSON。
     *
     * 两个维度都可能缺席：只配时段的指标（如「指定时段记录」）不带 category；
     * 两个都不适用时返回空串，后端会把它当作「未配置」落库。
     */
    buildParams() {
      const params = this.buildParamsFrom(this.selectedMetric, this.form)
      return params ? JSON.stringify(params) : ''
    },
    /**
     * 同上，但作用于任意「时段 / 类别」状态对象（单条件表单或组合条件的某一行）。
     *
     * 抽出来的原因：单条件与组合条件里的叶子必须是<strong>同一份</strong>参数拼装逻辑，
     * 否则同一个时段会出现两种写法，排查时根本分不清哪个生效。
     *
     * @returns {object|null} 可直接嵌进 rule_json 的对象；不适用时返回 null
     */
    buildParamsFrom(metric, state) {
      if (!metric) return null
      const params = {}
      if (metric.categoryConfigurable) {
        params.category = state.blockCategory || 'ANY'
      }
      if (metric.windowConfigurable) {
        const start = this.toMinutes(state.windowStart)
        const end = this.toMinutes(state.windowEnd)
        if (start === null || end === null) return null
        params.startMin = start
        params.endMin = end
      }
      return Object.keys(params).length ? params : null
    },
    /** 把后端返回的 params JSON 解析回表单字段；缺省回落到 00:00~05:00 / ANY。 */
    parseParams(params) {
      const fallback = { windowStart: '00:00', windowEnd: '05:00', blockCategory: 'ANY' }
      if (!params) return fallback
      try {
        const obj = JSON.parse(params)
        const start = Number(obj.startMin)
        const end = Number(obj.endMin)
        return {
          windowStart: Number.isNaN(start) ? fallback.windowStart : this.minutesToText(start),
          windowEnd: Number.isNaN(end) ? fallback.windowEnd : this.minutesToText(end),
          blockCategory: obj.category || fallback.blockCategory
        }
      } catch (err) {
        // 非法 params 当作未配置
        return fallback
      }
    },
    validate() {
      if (!this.form.code.trim()) return '请填写成就编码'
      if (!/^[A-Z][A-Z0-9_]{2,63}$/.test(this.form.code.trim().toUpperCase())) {
        return '成就编码只能是大写字母、数字、下划线，且以字母开头'
      }
      if (!this.form.name.trim()) return '请填写成就名称'
      if (!this.form.categoryCode) return '请选择分类'
      if (!this.isSingleMode) return this.validateRule()
      if (!this.form.metricKey) return '请选择判定指标'
      if (!this.form.targetValue || Number(this.form.targetValue) < 1) return '解锁阈值至少为 1'
      if (this.selectedMetric && this.selectedMetric.categoryConfigurable && !this.form.blockCategory) {
        return '请选择统计类别'
      }
      if (this.selectedMetric && this.selectedMetric.windowConfigurable) {
        const start = this.toMinutes(this.form.windowStart)
        const end = this.toMinutes(this.form.windowEnd)
        if (start === null || end === null) return '请选择统计时段'
        if (start >= end) return '时段的开始时间必须早于结束时间'
      }
      return ''
    },
    onSubmit() {
      if (this.saving) return
      const error = this.validate()
      if (error) {
        this.$message.error(error)
        return
      }
      this.saving = true
      const payload = this.buildPayload()
      const task = this.form.id
        ? updateAchievement(this.form.id, payload)
        : createAchievement(payload)

      task
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '保存失败')
          }
          this.$message.success(this.form.id ? '已保存' : '已创建')
          this.formVisible = false
          this.load()
        })
        .catch(err => {
          this.$message.error(err.message || '保存失败')
        })
        .finally(() => {
          this.saving = false
        })
    },
    onToggleStatus(row) {
      const enable = row.status !== 'ENABLED'
      const hint = enable
        ? `上架后「${row.name}」会立即参与评估，达到条件的用户会被解锁。`
        : `下架后「${row.name}」不再解锁新用户、也不再出现在徽章墙；已解锁用户的历史记录不受影响。`
      this.$confirm(hint, enable ? '上架成就' : '下架成就', {
        confirmButtonText: enable ? '确定上架' : '确定下架',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => changeAchievementStatus(row.id, enable))
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '操作失败')
          }
          this.$message.success(enable ? '已上架' : '已下架')
          this.load()
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || '操作失败')
        })
    },
    onDelete(row) {
      this.$confirm(
        `确定删除成就「${row.name}」（${row.code}）吗？该操作不可恢复。`,
        '删除成就',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
      )
        .then(() => deleteAchievement(row.id))
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '删除失败')
          }
          this.$message.success('已删除')
          this.load()
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || '删除失败')
        })
    },
    openPreview(row) {
      this.preview = null
      this.previewUserId = ''
      this.currentPreviewId = row.id
      this.previewVisible = true
    },
    onPreview() {
      const userId = String(this.previewUserId || '').trim()
      if (!userId) {
        this.$message.error('请填写用户 ID')
        return
      }
      this.previewing = true
      previewAchievement(this.currentPreviewId, userId)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '试算失败')
          }
          this.preview = res.data
        })
        .catch(err => {
          this.preview = null
          this.$message.error(err.message || '试算失败')
        })
        .finally(() => {
          this.previewing = false
        })
    }
  }
}
</script>

<style scoped>
/* 卡片 / 筛选栏 / 分页样式统一来自 @/styles/admin.css，这里只放本页私有结构 */
.adm-head-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

/*
 * 「删除」外面包了一层 el-tooltip，插入的 span 会打断相邻选择器 ——
 * 所以操作列间距不靠 `.el-link + .el-link`，改由 admin.css 的 `.adm-op-actions`
 * （flex + gap）统一控制，本页只留 tooltip 包裹层自己的布局。
 */
.adm-op-item {
  display: inline-flex;
}

.adm-w200 {
  width: 200px;
}

.adm-w140 {
  width: 140px;
}

.adm-w160 {
  width: 160px;
}

.adm-w180 {
  width: 180px;
}

.adm-w130 {
  width: 130px;
}

.adm-w-full {
  width: 100%;
}

.adm-inline-hint.is-inline {
  display: inline-block;
  margin-top: 0;
  margin-left: 10px;
}

.ach-head {
  display: flex;
  align-items: center;
}

.ach-icon {
  margin-right: 6px;
  color: rgba(0, 0, 0, 0.55);
}

.ach-name {
  margin-right: 6px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.88);
}

.level-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
  vertical-align: middle;
}

.cond-main {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.88);
}

.cond-tag {
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 4px;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
}

.cond-sub {
  margin-top: 2px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.inline-row {
  display: flex;
  gap: 10px;
}

.option-dim {
  float: right;
  margin-left: 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.metric-info {
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 12px;
  line-height: 1.9;
  color: rgba(0, 0, 0, 0.75);
}

.metric-dim {
  margin-left: 8px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.icon-preview {
  margin-top: 6px;
  color: rgba(0, 0, 0, 0.55);
}

.adm-preview-form {
  margin-top: 16px;
}

.adm-preview-result {
  margin-top: 16px;
}

.adm-preview-strong {
  font-size: 15px;
  color: rgba(0, 0, 0, 0.88);
}

.adm-preview-ok {
  color: #389e0d;
}

/* ---------------- 组合条件编辑器 ---------------- */

.rule-rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rule-row {
  padding: 10px 12px;
  border-radius: 6px;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.06);
}

.rule-row-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.rule-row-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.06);
  font-size: 12px;
  color: rgba(0, 0, 0, 0.65);
}

.rule-row-op {
  font-size: 12px;
  font-weight: 600;
  color: #409eff;
}

.rule-row-remove {
  margin-left: auto;
  font-size: 12px;
}

.rule-row-body {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.rule-metric {
  width: 240px;
}

.rule-target {
  width: 130px;
}

.rule-actions {
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
}
</style>
